import { useCallback, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router';
import { Box, Column, Row, Screen, Tabs, Typography } from '@tempmee/react';
import { BackButton, InputSelect } from '~/commonComponents';
import { DashboardJobsStatus, DashboardJobsType } from '~/domain/DashboardJobs';
import { getShouldEnforcePermanentJobV2 } from '~/services/posthog/FeatureFlagUtils';
import { fireShiftsOverviewActionNeededTabClicked } from '~/posthogs/events';
import { OpenTab, PendingTab, ScheduledTab } from './components';
import { FilterOptions } from './Constants';
import { TABS, TShiftListTab, useShiftList } from './hooks';
import { TJobListPageRoutes, TJobListPageRoutesLocationState } from './Types';

export const JobList = () => {
  const params = useParams<TJobListPageRoutes>();
  const location = useLocation() as Partial<{ state: Partial<TJobListPageRoutesLocationState> }>;

  const isEnforcePermanentJobV2 = useRef(getShouldEnforcePermanentJobV2()).current;

  const [filterJobType, setFilterJobType] = useState<DashboardJobsType>(DashboardJobsType.ALL);

  const { activeTab, dashboardInfo, onChangeTab } = useShiftList({
    type: (location.state?.type || params.type) as DashboardJobsStatus,
    jobType: filterJobType,
  });

  const countOpen = dashboardInfo?.openCount || 0;
  const countPending = dashboardInfo?.pendingCount || 0;
  const countScheduled = dashboardInfo?.filledCount || 0;

  const handleTabChanged = useCallback(
    (tab: string) => {
      if (tab === TABS.PENDING) {
        fireShiftsOverviewActionNeededTabClicked();
      }
      onChangeTab(tab as TShiftListTab);
    },
    [onChangeTab],
  );

  return (
    <Screen variant="standard">
      <Screen.Header
        start={<BackButton />}
        title={isEnforcePermanentJobV2 ? 'Shifts & Interviews' : 'Shifts Overview'}
      />
      <Screen.Body>
        {/* Screen's standard card already supplies the page gutter (p-10) and header gap,
            so the tab bar needs no margins — gap handles the rhythm down to the list. */}
        <Column gap={6}>
          <Row align="center" justify="between">
            <Tabs defaultValue={TABS.OPEN} value={activeTab} onValueChange={handleTabChanged}>
              <Tabs.List style={{ marginBottom: 0 }}>
                <Tabs.Tab
                  value={TABS.OPEN}
                  label={countOpen > 0 ? (countOpen > 99 ? '99+' : String(countOpen)) : undefined}
                  data-cy="open-shifts-tab"
                >
                  Open
                </Tabs.Tab>
                <Tabs.Tab
                  value={TABS.PENDING}
                  label={
                    countPending > 0
                      ? countPending > 99
                        ? '99+'
                        : String(countPending)
                      : undefined
                  }
                  labelVariant="error"
                  data-cy="action-needed-tab"
                >
                  Action Needed
                </Tabs.Tab>
                <Tabs.Tab
                  value={TABS.SCHEDULED}
                  label={
                    countScheduled > 0
                      ? countScheduled > 99
                        ? '99+'
                        : String(countScheduled)
                      : undefined
                  }
                  data-cy="scheduled-shifts-tab"
                >
                  Scheduled
                </Tabs.Tab>
              </Tabs.List>
            </Tabs>
            {isEnforcePermanentJobV2 && (
              <Row align="center" gap={2}>
                <Typography as="span" bold size="body_large">
                  Filter by:
                </Typography>
                <Box width="212px">
                  <InputSelect
                    isSelectVisible={false}
                    options={FilterOptions}
                    value={filterJobType}
                    setValue={(val) => setFilterJobType(val as DashboardJobsType)}
                  />
                </Box>
              </Row>
            )}
          </Row>
          {activeTab === TABS.OPEN && <OpenTab jobType={filterJobType} />}
          {activeTab === TABS.PENDING && <PendingTab jobType={filterJobType} />}
          {activeTab === TABS.SCHEDULED && <ScheduledTab jobType={filterJobType} />}
        </Column>
      </Screen.Body>
    </Screen>
  );
};
